import { type ChangeEvent, type FC, useRef } from "react";
import { useField } from "react-final-form";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import {
  useBannerLogic,
  BannerToggle,
  BannerPlaceholder,
  BannerPreview,
} from "@/shared/components/Form/BannerField";

const Root = styled("div")(({ theme }) => ({
  position: "relative",
  overflow: "hidden",
  borderRadius: 4,
  width: "100%",
  paddingTop: "56.25%", // фиксированный контейнер 16:9, внутри переключаются форматы
  height: 0,
  ...theme.applyStyles("dark", {
    border: "1px solid rgba(255, 255, 255, 0.23)",
  }),
  ...theme.applyStyles("light", {
    border: "1px solid rgba(0, 0, 0, 0.23)",
  }),
  "&:hover": {
    ...theme.applyStyles("dark", { borderColor: "rgba(255, 255, 255, 1)" }),
    ...theme.applyStyles("light", { borderColor: "rgba(0, 0, 0, 1)" }),
  },
}));

const ControlsBar = styled("div")({
  position: "absolute",
  right: 0,
  bottom: 0,
  left: 0,
  height: 51,
  backgroundColor: "rgba(0,0,0,0.9)",
  color: "white",
});

/**
 * Клон референсного ImageField (partner/components/ImageField), но как ОДИН
 * виджет-обёртка над двумя полями формы (previewId — 16:9, verticalPreviewId
 * — 2:3), а не два отдельных BannerField-слота. Переключение формата — тем же
 * ToggleButtonGroup сверху слева (BannerToggle), с зелёной точкой у форматов,
 * где уже есть картинка. Кроппер сознательно не подключаем (см. отчёт —
 * бэклог), поэтому вместо BannerControls — свой минимальный контролс-бар
 * с одной кнопкой удаления текущего формата.
 */
export const PreviewImageField: FC = () => {
  const { t } = useTranslation();
  const previewField = useField<string | null>("previewId");
  const verticalPreviewField = useField<string | null>("verticalPreviewId");
  const inputRef = useRef<HTMLInputElement>(null);

  const value = {
    horizontalId: previewField.input.value ?? undefined,
    verticalId: verticalPreviewField.input.value ?? undefined,
  };

  const handleChange = (next: {
    horizontalId?: string | null;
    verticalId?: string | null;
  }) => {
    if (next.horizontalId !== value.horizontalId) {
      previewField.input.onChange(next.horizontalId ?? null);
    }
    if (next.verticalId !== value.verticalId) {
      verticalPreviewField.input.onChange(next.verticalId ?? null);
    }
  };

  const {
    activeImageType,
    setActiveImageType,
    hover,
    currentImage,
    currentOrientation,
    createPreviewResult,
    hasImage,
    isEmpty,
    horizontalImage,
    verticalImage,
    handleFileChange,
    onDragEnter,
    onDragOver,
    onDragLeave,
    onDrop,
    onClickDelete,
  } = useBannerLogic({
    value,
    onChange: handleChange,
    successMessage: t("videos.previewField.uploadedSnackbar"),
  });

  const onClickUpload = () => inputRef.current?.click();

  const onChangeFileInput = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) handleFileChange(file);
    event.target.value = "";
  };

  return (
    <Root
      style={hover ? { borderColor: "currentColor" } : undefined}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <BannerToggle
        activeImageType={activeImageType}
        onImageTypeChange={setActiveImageType}
        hasHorizontalImage={Boolean(horizontalImage.value)}
        hasVerticalImage={Boolean(verticalImage.value)}
      />

      {isEmpty && (
        <BannerPlaceholder
          orientation={currentOrientation}
          label={t("videos.previewField.uploadLabel")}
          onUploadClick={onClickUpload}
          isLoading={createPreviewResult.loading}
        />
      )}

      {hasImage && currentImage && (
        <BannerPreview
          orientation={currentOrientation}
          imageUrl={currentImage}
          isLoading={createPreviewResult.loading}
        />
      )}

      {hasImage && (
        <ControlsBar>
          <Tooltip
            title={t("videos.previewField.deleteTooltip")}
            placement="top"
          >
            <IconButton
              onClick={onClickDelete}
              size="large"
              color="inherit"
              sx={{ position: "absolute", right: 1, bottom: 1 }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </ControlsBar>
      )}

      <Box
        component="input"
        accept="image/*"
        type="file"
        ref={inputRef}
        onChange={onChangeFileInput}
        sx={{
          position: "absolute",
          opacity: 0,
          inset: 0,
          pointerEvents: "none",
        }}
      />
    </Root>
  );
};
